import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import Dashboard from "./components/DispensorList";
import GunLogDetails from "./components/GundetailsPage";
import Configuration from "./components/Configuration";
import UserProfile from "./components/Profile";
import LiveLogs from "./components/general";
import DispenserDetailReusable from "./components/DispensorDetailsPage";
import RegisterPage from "./components/Register";
import LoginPage from "./components/LoginPage";
import Graphview from "./components/GraphView";
import NotFound from "./components/not-found";
import FirmwareUpdate from "./components/FirmwareUpdate";
import Navigation from "./components/Navigation";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import DispenserGuard from "./components/dispensorGuard";
import ChargerProfile from "./components/ChargerProfile";
import useSyncAuthAcrossTabs from "./components/AccessAcrossBrowser";
import ChargerAndQR from "./components/ChargerUpdates";
import Advertisers from "./components/Advertisers";
import Campaigns from "./components/Campaigns";

import { ConfigProvider, useConfig } from "./context/configContext";
import { ChargerProvider } from "./context/ChargerContext";
import { OnlineStatusProvider } from "./context/OnlineStatusContext";
import MapListContainer from "./components/mapListbutton/MapListContainer";

/* ---------------------- Sync Auth ---------------------- */
const SyncHandler = () => {
  useSyncAuthAcrossTabs();
  return null;
};

/* ---------------------- App ---------------------- */
const App = () => {
  return (
    <ConfigProvider>
      <ChargerProvider>
        <OnlineStatusProvider>
          <BrowserRouter>
            <SyncHandler />
            <Toaster position="top-right" />
            <AppRoutes />
          </BrowserRouter>
        </OnlineStatusProvider>
      </ChargerProvider>
    </ConfigProvider>
  );
};

/* ---------------------- Routes ---------------------- */
const AppRoutes = () => {
  const { config, setConfig } = useConfig();

  const handleConfigChange = (newConfig) => {
    setConfig(newConfig);
  };

  return (
    <Routes>
      {/* ===================== AUTH ROUTES (NO SIDEBAR) ===================== */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* ===================== MAIN LAYOUT ===================== */}
      <Route element={<Navigation />}>
        {/* Dashboard */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DispenserGuard>
                <Dashboard />
              </DispenserGuard>
            </ProtectedRoute>
          }
        />

        {/* Maps */}
        <Route
          path="/maps"
          element={
            <ProtectedRoute>
              <MapListContainer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ChargerProfile"
          element={
            <ProtectedRoute>
              <ChargerProfile />
            </ProtectedRoute>
          }
        />
        {/* Dispenser Details */}
        <Route
          path="/:station_id/:dispensor_id"
          element={
            <ProtectedRoute>
              <DispenserGuard>
                <DispenserDetailReusable />
              </DispenserGuard>
            </ProtectedRoute>
          }
        />

        {/* Graph */}
        <Route
          path="/graphView/:station_id/:dispensor_id"
          element={
            <ProtectedRoute>
              <DispenserGuard>
                <Graphview />
              </DispenserGuard>
            </ProtectedRoute>
          }
        />

        {/* Logs */}
        <Route
          path="/general"
          element={
            <ProtectedRoute>
              <DispenserGuard>
                <LiveLogs />
              </DispenserGuard>
            </ProtectedRoute>
          }
        />

        <Route
          path="/logs/:lognum"
          element={
            <ProtectedRoute>
              <DispenserGuard>
                <GunLogDetails />
              </DispenserGuard>
            </ProtectedRoute>
          }
        />

        <Route
          path="/chargerUiUpdate"
          element={
            <ProtectedRoute>
              <DispenserGuard>
                <ChargerAndQR />
              </DispenserGuard>
            </ProtectedRoute>
          }
        />

        {/* Config */}
        <Route
          path="/config"
          element={
            <ProtectedRoute>
              <DispenserGuard>
                <Configuration
                  dispensers={config.dispensers}
                  guns={config.guns}
                  onSave={handleConfigChange}
                />
              </DispenserGuard>
            </ProtectedRoute>
          }
        />

        {/* Profile */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          }
        />

        {/* Device Setup */}
        <Route
          path="/device-setup"
          element={
            <ProtectedRoute>
              <FirmwareUpdate />
            </ProtectedRoute>
          }
        />

        {/* Advertisers */}
        <Route
          path="/advertisers"
          element={
            <ProtectedRoute>
              <Advertisers />
            </ProtectedRoute>
          }
        />

        {/* Campaigns */}
        <Route
          path="/campaigns"
          element={
            <ProtectedRoute>
              <Campaigns />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* ===================== FALLBACK ===================== */}
      <Route path="/not-found" element={<NotFound />} />
      <Route path="*" element={<Navigate to="/not-found" replace />} />
    </Routes>
  );
};

export default App;
